<template>
  <Head title="Detail Gallery" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <div>
        <Breadcrumbs
          :breadcrumbs="[
            { name: 'Dashboard', href: route('dashboard') },
            {
              name: `Gallery`,
              href: route('gallery.index'),
            },
            {
              name: `${gallery.data.title}`,
            },
          ]"
        />

        <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
          <!-- Detail Card -->
          <div class="order-2 col-span-1 space-y-6 lg:order-1">
            <div class="bg-white border border-gray-200">
              <div
                class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-rose-800 to-yellow-600 sm:px-6 sm:py-4"
              >
                <h2
                  class="flex items-center text-lg font-bold text-white sm:text-xl"
                >
                  <i class="mr-2 fa-solid fa-images sm:mr-3"></i>

                  {{ gallery.data.title }}
                </h2>
              </div>
              <div class="p-4 sm:p-6">
                <dl class="space-y-4">
                  <div>
                    <dt class="text-sm font-medium text-gray-500">
                      Judul Gallery
                    </dt>
                    <dd class="mt-1 text-sm font-semibold text-gray-900">
                      {{ gallery.data.title }}
                    </dd>
                  </div>

                  <div>
                    <dt class="text-sm font-medium text-gray-500">Deskripsi</dt>
                    <dd class="mt-1 text-sm text-gray-900">
                      {{ gallery.data.description || "Tidak ada deskripsi" }}
                    </dd>
                  </div>

                  <div>
                    <dt class="text-sm font-medium text-gray-500">
                      Jumlah Gambar
                    </dt>
                    <dd class="mt-1">
                      <span
                        :class="
                          (Array.isArray(gallery.data.image_path)
                            ? gallery.data.image_path.length
                            : 0) > 0
                            ? 'inline-flex items-center px-2 py-1 text-xs font-medium text-green-800 bg-green-100 rounded-full'
                            : 'inline-flex items-center px-2 py-1 text-xs font-medium text-gray-800 bg-gray-100 rounded-full'
                        "
                      >
                        <i class="mr-1 fa-solid fa-images"></i>
                        {{
                          Array.isArray(gallery.data.image_path)
                            ? gallery.data.image_path.length
                            : 0
                        }}
                        foto
                      </span>
                    </dd>
                  </div>
                </dl>

                <!-- Action Buttons -->
                <div
                  class="pt-4 mt-6 border-t border-gray-200"
                  v-if="$page.props.auth.user"
                >
                  <button
                    @click="editDokumen(gallery.data.idgallery)"
                    class="inline-flex items-center justify-center w-full px-4 py-2 text-sm font-medium text-indigo-600 transition-colors duration-200 bg-indigo-100 border border-transparent rounded-lg hover:bg-indigo-200 hover:text-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <i class="mr-2 fa-solid fa-pen"></i>
                    Edit Gallery
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Images Section -->
          <div class="order-1 col-span-1 lg:col-span-2 lg:order-2">
            <!-- Header Section -->
            <div class="mb-4 sm:mb-6">
              <div
                class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
              >
                <div>
                  <h3 class="text-lg font-semibold text-gray-900">
                    Koleksi Gambar
                  </h3>
                  <p class="mt-1 text-sm text-gray-600">
                    Gallery berisi
                    {{
                      Array.isArray(gallery.data.image_path)
                        ? gallery.data.image_path.length
                        : 0
                    }}
                    gambar
                  </p>
                </div>
                <div class="flex items-center mt-3 space-x-2 sm:mt-0">
                  <label for="perPage" class="text-sm text-gray-600"
                    >Per halaman:</label
                  >
                  <select
                    id="perPage"
                    v-model.number="perPage"
                    class="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-rose-500"
                  >
                    <option :value="5">5</option>
                    <option :value="10">10</option>
                    <option :value="20">20</option>
                    <option :value="50">50</option>
                  </select>
                </div>
              </div>
            </div>

            <!-- Images Table -->
            <div
              v-if="images.length > 0"
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5"
            >
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-300">
                  <thead class="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      >
                        #
                      </th>
                      <th
                        scope="col"
                        class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6 min-w-[120px]"
                      >
                        Preview
                      </th>
                      <th
                        scope="col"
                        class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 hidden md:table-cell min-w-[200px]"
                      >
                        Nama File
                      </th>
                      <th
                        scope="col"
                        class="relative py-3.5 pl-3 pr-4 sm:pr-6 min-w-[120px]"
                      >
                        <span class="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr
                      v-for="(img, idx) in paginatedImages"
                      :key="img + '-' + ((page - 1) * perPage + idx)"
                      class="transition-colors duration-150 hover:bg-gray-50"
                    >
                      <td
                        class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 whitespace-nowrap sm:pl-6"
                      >
                        {{ (page - 1) * perPage + idx + 1 }}
                      </td>
                      <td class="px-3 py-4 text-sm">
                        <div
                          class="relative cursor-pointer group"
                          @click="openImageModal(img)"
                        >
                          <img
                            :src="img"
                            :alt="`Gallery Image ${idx + 1}`"
                            class="object-cover w-16 h-16 transition-opacity duration-200 border border-gray-200 rounded-lg shadow-sm group-hover:opacity-75"
                          />
                          <div
                            class="absolute inset-0 flex items-center justify-center transition-all duration-200 bg-black bg-opacity-0 rounded-lg group-hover:bg-opacity-30"
                          >
                            <i
                              class="text-white transition-opacity opacity-0 fa-solid fa-magnifying-glass group-hover:opacity-100"
                            ></i>
                          </div>
                        </div>
                        <!-- Mobile view: Show filename -->
                        <div
                          class="mt-1 text-xs text-gray-500 truncate md:hidden"
                        >
                          {{ img.split("/").pop() }}
                        </div>
                      </td>
                      <td
                        class="hidden px-3 py-4 text-sm text-gray-500 md:table-cell"
                      >
                        <div
                          class="max-w-xs truncate"
                          :title="img.split('/').pop()"
                        >
                          {{ img.split("/").pop() }}
                        </div>
                      </td>
                      <td
                        class="relative py-4 pl-3 pr-4 text-sm font-medium text-right sm:pr-6"
                      >
                        <div
                          class="flex items-center justify-end space-x-1 sm:space-x-2"
                        >
                          <!-- Action Buttons -->
                          <button
                            @click="
                              editFile(
                                gallery.data.idgallery,
                                (page - 1) * perPage + idx
                              )
                            "
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-indigo-600 bg-indigo-100 rounded-md hover:bg-indigo-200 hover:text-indigo-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Edit"
                          >
                            <i class="w-3 h-3 fa-solid fa-pen"></i>
                            <span class="hidden ml-1 sm:inline">Edit</span>
                          </button>
                          <button
                            @click="
                              confirmDeleteFile(
                                gallery.data.idgallery,
                                (page - 1) * perPage + idx,
                                img
                              )
                            "
                            class="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-md hover:bg-red-200 hover:text-red-700 transition-colors duration-150 sm:px-2.5 sm:py-1.5"
                            title="Delete"
                          >
                            <i class="w-3 h-3 fa-solid fa-trash"></i>
                            <span class="hidden ml-1 sm:inline">Hapus</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <!-- Pagination Controls -->
              <div
                class="px-4 py-3 bg-white border-t border-gray-200 sm:px-6"
                v-if="totalPages > 1"
              >
                <div class="flex items-center justify-between">
                  <div class="flex justify-between flex-1 sm:hidden">
                    <button
                      @click="prevPage"
                      :disabled="page === 1"
                      class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      Sebelumnya
                    </button>
                    <button
                      @click="nextPage"
                      :disabled="page === totalPages"
                      class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                    >
                      Berikutnya
                    </button>
                  </div>
                  <div
                    class="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between"
                  >
                    <div class="text-sm text-gray-600">
                      Menampilkan {{ startCount }}–{{ endCount }} dari
                      {{ images.length }}
                    </div>
                    <div>
                      <nav
                        class="inline-flex -space-x-px rounded-md shadow-sm isolate"
                        aria-label="Pagination"
                      >
                        <button
                          @click="prevPage"
                          :disabled="page === 1"
                          class="relative inline-flex items-center px-2 py-2 text-gray-400 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50"
                        >
                          <span class="sr-only">Sebelumnya</span>
                          <i class="w-4 h-4 fa-solid fa-chevron-left"></i>
                        </button>
                        <button
                          v-for="p in totalPages"
                          :key="p"
                          @click="goToPage(p)"
                          :class="[
                            'relative inline-flex items-center px-4 py-2 text-sm font-medium border',
                            p === page
                              ? 'z-10 bg-rose-600 border-rose-600 text-white'
                              : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50',
                          ]"
                        >
                          {{ p }}
                        </button>
                        <button
                          @click="nextPage"
                          :disabled="page === totalPages"
                          class="relative inline-flex items-center px-2 py-2 text-gray-400 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50"
                        >
                          <span class="sr-only">Berikutnya</span>
                          <i class="w-4 h-4 fa-solid fa-chevron-right"></i>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Empty State -->
            <div
              v-else
              class="overflow-hidden bg-white shadow ring-1 ring-black ring-opacity-5 md:rounded-lg"
            >
              <div class="py-12 text-center">
                <div class="flex justify-center">
                  <i class="text-4xl text-gray-400 fa-solid fa-images"></i>
                </div>
                <h3 class="mt-4 text-lg font-medium text-gray-900">
                  Tidak ada gambar
                </h3>
                <p class="mt-2 text-sm text-gray-500">
                  Gallery ini belum memiliki gambar.
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Image Preview Modal -->
        <div
          v-if="showImageModal"
          class="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          @click="closeImageModal"
        >
          <div class="relative max-w-4xl max-h-full p-4">
            <img
              :src="selectedImage"
              alt="Full Size Image"
              class="object-contain max-w-full max-h-full rounded-lg shadow-2xl"
            />
            <button
              @click="closeImageModal"
              class="absolute text-white transition-colors duration-200 top-4 right-4 hover:text-gray-300"
            >
              <i class="text-2xl fa-solid fa-times"></i>
            </button>
          </div>
        </div>

        <!-- Modals -->
        <EditGalleryModal
          v-model:isOpen="isModalOpen"
          :title="'Edit Gallery'"
          :gallery="datagallery"
        />
        <EditFileModal
          v-model:isOpen="isModalFile"
          :title="'Edit File'"
          :gallery="datagallery"
          :fileIndex="selectedFileIndex"
        />
      </div>
    </template>
  </Container>
</template>

<script setup>
import { Head, router } from "@inertiajs/vue3";
import { defineProps, ref, computed, watch } from "vue";
import EditGalleryModal from "@/Components/Gallery/EditGalleryModal.vue";
import EditFileModal from "@/Components/Gallery/EditFileModal.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Swal from "sweetalert2";

const props = defineProps({
  gallery: {
    type: Object,
    required: true,
  },
});

const isModalOpen = ref(false);
const isModalFile = ref(false);
const datagallery = ref(props.gallery?.data || null); // Initialize with current gallery data
const showImageModal = ref(false);
const selectedImage = ref("");
const selectedFileIndex = ref(0);

// Images and pagination state
const images = computed(() =>
  Array.isArray(props.gallery?.data?.image_path)
    ? props.gallery.data.image_path
    : []
);
const page = ref(1);
const perPage = ref(5);
const totalPages = computed(() =>
  Math.max(1, Math.ceil(images.value.length / perPage.value))
);
const paginatedImages = computed(() => {
  const start = (page.value - 1) * perPage.value;
  return images.value.slice(start, start + perPage.value);
});

const startCount = computed(() =>
  images.value.length ? (page.value - 1) * perPage.value + 1 : 0
);
const endCount = computed(() =>
  Math.min(page.value * perPage.value, images.value.length)
);

function goToPage(p) {
  if (p >= 1 && p <= totalPages.value) page.value = p;
}
function nextPage() {
  if (page.value < totalPages.value) page.value += 1;
}
function prevPage() {
  if (page.value > 1) page.value -= 1;
}

// Ensure current page is valid if images change (e.g., after delete)
watch(images, () => {
  if (page.value > totalPages.value) page.value = totalPages.value;
});

// Reset to first page when perPage changes
watch(perPage, () => {
  page.value = 1;
});

// Image preview modal functions
const openImageModal = (imageSrc) => {
  selectedImage.value = imageSrc;
  showImageModal.value = true;
};

const closeImageModal = () => {
  showImageModal.value = false;
  selectedImage.value = "";
};

const editDokumen = async (idgallery) => {
  console.log("Edit button clicked for gallery ID:", idgallery);
  console.log("Current gallery props:", props.gallery);

  // Always start with current gallery data as fallback
  if (props.gallery && props.gallery.data) {
    datagallery.value = { ...props.gallery.data }; // Create a copy
    console.log("Using current gallery data:", datagallery.value);
    isModalOpen.value = true;
    return;
  }

  // If no props data, show error
  Swal.fire({
    title: "Error!",
    text: "Data gallery tidak tersedia",
    icon: "error",
    confirmButtonText: "OK",
  });
};

const editFile = async (idgallery, idx) => {
  try {
    const response = await fetch(`/galleryedit/${idgallery}`);
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();

    datagallery.value = data.gallery;
    selectedFileIndex.value = idx;
    isModalFile.value = true;
  } catch (error) {
    console.error("Error fetching data:", error.message);
    Swal.fire({
      title: "Error!",
      text: "Gagal memuat data file",
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};

const confirmDeleteFile = async (idgallery, idx, imageUrl) => {
  const fileName = imageUrl.split("/").pop();

  const result = await Swal.fire({
    title: "Apakah Anda yakin?",
    text: `File "${fileName}" akan dihapus permanen!`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, hapus!",
    cancelButtonText: "Batal",
  });

  if (result.isConfirmed) {
    await deleteFile(idgallery, idx);
  }
};

const deleteFile = async (idgallery, idx) => {
  try {
    // Show loading
    Swal.fire({
      title: "Menghapus...",
      text: "Sedang menghapus file",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    const response = await fetch(`/galleryhapus/${idgallery}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-TOKEN": document
          .querySelector('meta[name="csrf-token"]')
          ?.getAttribute("content"),
      },
      body: JSON.stringify({ index: idx }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    // Update gallery data after deletion
    props.gallery.data.image_path.splice(idx, 1);

    Swal.fire({
      title: "Terhapus!",
      text: "File berhasil dihapus",
      icon: "success",
      confirmButtonText: "OK",
    });

    // Refresh page to update data
    setTimeout(() => {
      router.reload({ only: ["gallery"] });
    }, 1000);
  } catch (error) {
    console.error("Error deleting file:", error.message);
    Swal.fire({
      title: "Error!",
      text: "Gagal menghapus file. Silakan coba lagi.",
      icon: "error",
      confirmButtonText: "OK",
    });
  }
};
</script>
